// /client/src/components/CateringFormContent.js
import React, { useState, useEffect } from "react";
import { useSearchParams } from 'react-router-dom';
import './ContactContent.css';

const CateringFormContent = ({
    className,
    title,
    subtitle,
    colorMode
}) => {
    const [searchParams] = useSearchParams();
    
    // Function to map query parameter to form value
    const getCateringOptionFromQuery = (queryType) => {
        const mapping = {
            'pickup': 'Pickup Catering',
            'delivery': 'Delivery Catering',
            'onsite': 'On-Site Catering'
        };
        return mapping[queryType] || '';
    };

    // Location phone number mapping
    const getLocationPhone = (location) => {
        const locationPhones = {
            'Brentwood': '(925) 308-7997',
            'Concord': '(925) 825-2333',
            'Hercules': '(510) 724-2332',
            'Martinez': '(925) 957-1400',
            'Pleasant Hill': '(925) 798-2333',
            'Walnut Creek': '(925) 952-9393'
        };
        return locationPhones[location] || null;
    };

    // Get initial catering option from URL query parameter
    const initialCateringOption = getCateringOptionFromQuery(searchParams.get('type'));

    const [formData, setFormData] = useState({
        cateringOption: initialCateringOption,
        kindersLocation: '',
        eventDate: '',
        eventType: '',
        eventLocation: '',
        guestSize: '',
        eventDetails: '',
        companyName: '',
        firstName: '',
        lastName: '',
        emailAddress: '',
        confirmEmail: '',
        phoneNumber: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [submittedLocation, setSubmittedLocation] = useState('');
    const [emailMismatch, setEmailMismatch] = useState(false);

    // Update catering option when URL parameter changes
    useEffect(() => {
        const typeParam = searchParams.get('type');
        const cateringOption = getCateringOptionFromQuery(typeParam);
        if (cateringOption) {
            setFormData(prev => ({
                ...prev,
                cateringOption: cateringOption
            }));
        }
    }, [searchParams]);

    // Environment-aware API URL configuration
    const getApiUrl = () => {
        const reactAppEnv = process.env.REACT_APP_ENVIRONMENT;
        const nodeEnv = process.env.NODE_ENV;
        
        if (reactAppEnv === 'production' || nodeEnv === 'production') {
            return '/api/catering';
        } else {
            return 'http://localhost:5001/api/catering';
        }
    };

    const handleInputChange = (event) => {
        const { name, value, type, checked } = event.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Clear email mismatch error when user starts typing
        if (name === 'emailAddress' || name === 'confirmEmail') {
            setEmailMismatch(false);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsSubmitting(true);
        setSubmitMessage('');
        setEmailMismatch(false);

        // Validate email confirmation
        if (formData.emailAddress !== formData.confirmEmail) {
            setEmailMismatch(true);
            setSubmitMessage('Email addresses do not match. Please check and try again.');
            setIsSubmitting(false);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        try {
            const apiUrl = getApiUrl();
            
            // Remove confirmEmail before sending to API
            const { confirmEmail, ...dataToSend } = formData;
            
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(dataToSend)
            });

            if (response.ok) {
                // Store the submitted location before resetting
                setSubmittedLocation(formData.kindersLocation);
                // Show success message
                setIsFormSubmitted(true);
                // Reset form but keep the catering option from URL
                const initialCateringOption = getCateringOptionFromQuery(searchParams.get('type'));
                setFormData({
                    cateringOption: initialCateringOption,
                    kindersLocation: '',
                    eventDate: '',
                    eventType: '',
                    eventLocation: '',
                    guestSize: '',
                    eventDetails: '',
                    companyName: '',
                    firstName: '',
                    lastName: '',
                    emailAddress: '',
                    confirmEmail: '',
                    phoneNumber: ''
                });
            } else {
                // Enhanced error handling
                let errorMessage = 'Sorry, there was an error sending your catering request.';
                
                try {
                    const errorData = await response.json();
                    if (errorData.details && Array.isArray(errorData.details)) {
                        errorMessage = `Error: ${errorData.details.join(', ')}`;
                    } else if (errorData.error) {
                        errorMessage = errorData.error;
                    }
                } catch (parseError) {
                    if (response.status === 400) {
                        errorMessage = 'Please check your form data and try again.';
                    } else if (response.status === 429) {
                        errorMessage = 'Too many requests. Please wait a moment before trying again.';
                    } else if (response.status === 500) {
                        errorMessage = 'Server error. Please try again later.';
                    }
                }

                setSubmitMessage(errorMessage);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        } catch (error) {
            console.error('Network error:', error);
            
            let errorMessage = '';
            if (error.name === 'TypeError' && error.message.includes('fetch')) {
                errorMessage = 'Cannot connect to server. Please check your connection and try again.';
            } else {
                errorMessage = `Network error: ${error.message}`;
            }
            
            setSubmitMessage(errorMessage);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const showKindersLocation = formData.cateringOption === 'Delivery Catering' || formData.cateringOption === 'Pickup Catering';
    const showEventLocation = formData.cateringOption === 'On-Site Catering';

    // Generate contact information based on selected location
    const getContactInfo = () => {
        // Use submitted location if form was submitted, otherwise current form data
        const locationToUse = isFormSubmitted ? submittedLocation : formData.kindersLocation;
        const locationPhone = getLocationPhone(locationToUse);
        
        if (locationToUse && locationPhone) {
            // Remove phone number formatting for tel: link
            const phoneForTel = locationPhone.replace(/[^\d]/g, '');
            return {
                text: `call the ${locationToUse} location at`,
                phone: locationPhone,
                telLink: `tel:+1${phoneForTel}`
            };
        } else {
            // Fallback when no location or On-Site Catering
            return {
                text: 'call our On-site catering office at',
                phone: '(925) 825-2112',
                telLink: 'tel:+19258252112'
            };
        }
    };

    return (
        <>
        <div className={`column is-flex is-flex-direction-column ${className}`}>
            <div className="m-0 p-0 columns is-desktop">
                <div className="column is-12-mobile is-offset-0-mobile">
                    <h1 className="mx-0 mt-0 mb-2 p-0 h1 colorBlack is-align-center">{title}</h1>
                    <p className="mt-4 mb-5 is-align-center">{subtitle}</p>
                    <div className="m-0 py-4 px-6 white-block contact-form-container" id="message">
                        <h2 className="mt-4 mb-2 mx-0 p-0 h2">
                            {isFormSubmitted ? 'Catering Message Sent' : 'Catering Quote Form'}
                        </h2>

                        {/* Error Banner */}
                        {(submitMessage || emailMismatch) && !isFormSubmitted && (
                            <div className="mb-4 p-4 br backgroundColorError">
                                <p className="small colorWhite">
                                    {emailMismatch 
                                        ? 'Email addresses do not match. Please check and try again.' 
                                        : submitMessage}
                                </p>
                            </div>
                        )}

                        {/* Show success message or form */}
                        {isFormSubmitted ? (
                            <div className="my-4">
                                <p>Thanks for reaching out to Kinder's for your catering needs! We truly appreciate you considering us for your special event.</p>
                                
                                <p>A member of our catering team will review your request and get back to you within 24 hours with a custom quote tailored to your event.</p>
                                
                                {(() => {
                                    const contactInfo = getContactInfo();
                                    if (contactInfo.phone) {
                                        return (
                                            <p>For immediate assistance or questions about your catering request, {contactInfo.text} <a href={contactInfo.telLink}>{contactInfo.phone}</a>.</p>
                                        );
                                    } else {
                                        return (
                                            <p>For immediate assistance or questions about your catering request, {contactInfo.text} <a href={contactInfo.telLink}>{contactInfo.phone}</a>.</p>
                                        );
                                    }
                                })()}
                                
                                <p>Thank you,<br />The Kinder's Catering Team</p>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="contact-form">
                                {/* Catering Option */}
                                <div className="field py-2">
                                    <label className="mt-0 mb-1 p-0 label colorBlack">Catering Option *</label>
                                    <div className="m-0 p-0 control">
                                        <div className="m-0 p-0 location-selector-container">
                                            <select 
                                                className="location-selector"
                                                name="cateringOption" 
                                                value={formData.cateringOption} 
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="">Select catering option...</option>
                                                <option value="Delivery Catering">Delivery Catering</option>
                                                <option value="Pickup Catering">Pickup Catering</option>
                                                <option value="On-Site Catering">On-Site Catering</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* Conditional Kinder's Location Field */}
                                {showKindersLocation && (
                                    <div className="field pb-3">
                                        <label className="mt-0 mb-1 p-0 label colorBlack">Kinder's Location *</label>
                                        <div className="control">
                                            <div className="m-0 p-0 location-selector-container">
                                                <select 
                                                    className="location-selector"
                                                    name="kindersLocation" 
                                                    value={formData.kindersLocation} 
                                                    onChange={handleInputChange}
                                                    required={showKindersLocation}
                                                >
                                                    <option value="">Select location...</option>
                                                    <option value="Brentwood">Brentwood</option>
                                                    <option value="Concord">Concord</option>
                                                    <option value="Hercules">Hercules</option>
                                                    <option value="Martinez">Martinez</option>
                                                    <option value="Pleasant Hill">Pleasant Hill</option>
                                                    <option value="Walnut Creek">Walnut Creek</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Event Date */}
                                <div className="field pb-3">
                                    <label className="m-0 p-0 label colorBlack">Event Date</label>
                                    <div className="control">
                                        <input 
                                            className="input"
                                            type="date"
                                            name="eventDate"
                                            value={formData.eventDate}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </div>

                                {/* Event Type */}
                                <div className="field pb-3">
                                    <label className="mt-0 mb-1 p-0 label colorBlack">Event Type</label>
                                    <div className="control">
                                        <div className="m-0 p-0 location-selector-container">
                                            <select 
                                                className="location-selector"
                                                name="eventType" 
                                                value={formData.eventType} 
                                                onChange={handleInputChange}
                                            >
                                                <option value="">Select event type...</option>
                                                <option value="Luncheon">Luncheon</option>
                                                <option value="Banquet">Banquet</option>
                                                <option value="Picnic">Picnic</option>
                                                <option value="Wedding">Wedding</option>
                                                <option value="Rehearsal Dinner">Rehearsal Dinner</option>
                                                <option value="Shower">Shower</option>
                                                <option value="Other">Other</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* Conditional Event Location Field */}
                                {showEventLocation && (
                                    <div className="field pb-3">
                                        <label className={`m-0 p-0 label colorBlack`}>Event Location *</label>
                                        <div className="control">
                                            <input 
                                                className="input"
                                                type="text"
                                                name="eventLocation"
                                                value={formData.eventLocation}
                                                onChange={handleInputChange}
                                                required={showEventLocation}
                                                placeholder="Enter your event location"
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Guest Size */}
                                <div className="field pb-3">
                                    <label className={`m-0 p-0 label colorBlack`}>Guest Size *</label>
                                    <div className="control">
                                        <input 
                                            className="input"
                                            type="number"
                                            name="guestSize"
                                            value={formData.guestSize}
                                            onChange={handleInputChange}
                                            required
                                            min="1"
                                            max="10000"
                                            placeholder="Number of guests"
                                        />
                                    </div>
                                </div>

                                {/* Event Details */}
                                <div className="field pb-3">
                                    <label className="m-0 p-0 label colorBlack">Event Details</label>
                                    <div className="control">
                                        <textarea 
                                            className="textarea"
                                            name="eventDetails"
                                            value={formData.eventDetails}
                                            onChange={handleInputChange}
                                            rows="3"
                                            placeholder="Tell us about your event, menu preferences, dietary restrictions, etc. (optional)"
                                        ></textarea>
                                    </div>
                                </div>

                                {/* Company Name */}
                                <div className="field pb-3">
                                    <label className="m-0 p-0 label colorBlack">Company Name</label>
                                    <div className="control">
                                        <input 
                                            className="input"
                                            type="text"
                                            name="companyName"
                                            value={formData.companyName}
                                            onChange={handleInputChange}
                                            placeholder="Company name (optional)"
                                        />
                                    </div>
                                </div>

                                {/* First Name */}
                                <div className="field pb-3">
                                    <label className={`m-0 p-0 label colorBlack`}>First Name *</label>
                                    <div className="control">
                                        <input 
                                            className="input"
                                            type="text"
                                            name="firstName"
                                            value={formData.firstName}
                                            onChange={handleInputChange}
                                            required
                                            placeholder="Enter your first name"
                                        />
                                    </div>
                                </div>

                                {/* Last Name */}
                                <div className="field pb-3">
                                    <label className={`m-0 p-0 label colorBlack`}>Last Name *</label>
                                    <div className="control">
                                        <input 
                                            className="input"
                                            type="text"
                                            name="lastName"
                                            value={formData.lastName}
                                            onChange={handleInputChange}
                                            required
                                            placeholder="Enter your last name"
                                        />
                                    </div>
                                </div>

                                {/* Email Address */}
                                <div className="field pb-3">
                                    <label className={`m-0 p-0 label ${emailMismatch ? 'colorError' : 'colorBlack'}`}>Email Address *</label>
                                    <div className="control">
                                        <input 
                                            className={`input ${emailMismatch ? 'colorError' : ''}`}
                                            style={emailMismatch ? { borderColor: '#ff0000', color: '#ff0000' } : {}}
                                            type="email"
                                            name="emailAddress"
                                            value={formData.emailAddress}
                                            onChange={handleInputChange}
                                            required
                                            placeholder="Enter your email address"
                                        />
                                    </div>
                                    {emailMismatch && (
                                        <p className="ml-1 pt-1 small colorError">Email addresses do not match.</p>
                                    )}
                                </div>

                                {/* Confirm Email Address */}
                                <div className="field pb-3">
                                    <label className={`m-0 p-0 label ${emailMismatch ? 'colorError' : 'colorBlack'}`}>Confirm Email Address *</label>
                                    <div className="control">
                                        <input 
                                            className={`input ${emailMismatch ? 'colorError' : ''}`}
                                            style={emailMismatch ? { borderColor: '#ff0000', color: '#ff0000' } : {}}
                                            type="email"
                                            name="confirmEmail"
                                            value={formData.confirmEmail}
                                            onChange={handleInputChange}
                                            required
                                            placeholder="Re-enter your email address"
                                        />
                                    </div>
                                    {emailMismatch && (
                                        <p className="ml-1 pt-1 small colorError">Email addresses do not match.</p>
                                    )}
                                </div>

                                {/* Phone Number */}
                                <div className="field pb-3">
                                    <label className={`m-0 p-0 label colorBlack`}>Phone Number *</label>
                                    <div className="control">
                                        <input 
                                            className="input"
                                            type="tel"
                                            name="phoneNumber"
                                            value={formData.phoneNumber}
                                            onChange={handleInputChange}
                                            required
                                            placeholder="Enter your phone number"
                                        />
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <div className="field">
                                    <div className="control">
                                        <button 
                                            className="button"
                                            type="submit"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? 'Sending Request...' : 'Request Quote'}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </div>
        </>
    );
}

export default CateringFormContent;